import { useState, useCallback } from 'react';
import { validateEmail, parsePreRegisterError } from '../../utils/registration/registrationHelpers.ts';

export interface EmailValidationResult {
  isNewUser?: boolean;
  isPreRegistered?: boolean;
  isAlreadyValidated?: boolean;
  shouldRedirectToLogin?: boolean;
}

interface UseEmailValidationParams {
  createPreRegister: (email: string) => Promise<any>;
  setEmail: (email: string) => void;
  setStep: (step: 'email' | 'code' | 'password') => void;
  preRegisterLoading: boolean;
}

export const useEmailValidation = ({
  createPreRegister,
  setEmail,
  setStep,
  preRegisterLoading,
}: UseEmailValidationParams) => {
  const [emailError, setEmailError] = useState<string>('');
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);

  const handleEmailSubmit = useCallback(async (
    email: string
  ): Promise<EmailValidationResult> => {
    const validation = validateEmail(email);
    
    if (!validation.isValid) {
      setEmailError(validation.error || '');
      throw new Error(validation.error);
    }

    setIsSubmitting(true);
    setEmailError('');

    try {
      await createPreRegister(email);
      
      setEmail(email);
      setStep('code');
      
      return { isNewUser: true };
    } catch (error: any) {
      const parsedError = parsePreRegisterError(error);
      
      if (parsedError.shouldRedirectToLogin) {
        setEmailError(parsedError.message);
        return { shouldRedirectToLogin: true };
      }

      if (parsedError.isPreRegistered) {
        setEmail(email);
        
        if (parsedError.isAlreadyValidated) {
          setStep('password');
          return { isPreRegistered: true, isAlreadyValidated: true };
        } else {
          setStep('code');
          return { isPreRegistered: true };
        }
      }

      setEmailError(parsedError.message);
      throw error;
    } finally {
      setIsSubmitting(false);
    }
  }, [createPreRegister, setEmail, setStep]);

  return {
    handleEmailSubmit,
    emailError,
    isSubmitting: isSubmitting || preRegisterLoading,
    setEmailError,
  };
};
